import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fingerprint } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { addCredential, listCredentials } from '@/api/webauthn'
import { isPasskeyNudgeSnoozed, snoozePasskeyNudge } from '@/auth/storage'
import { getErrorMessage, isAbortedCeremony } from '@/lib/errors'
import { Button } from './Button'
import { ErrorAlert } from './ErrorAlert'
import { Modal } from './Modal'
import { useNotify } from './Notify'
import './PasskeyNudge.css'

const DEFAULT_KEY_NAME = 'этот браузер'

// Настойчивое, но не бесконечное напоминание: показывается на любой странице, пока у
// пользователя нет ни одного ключа, и откладывается ровно на сутки. Привязать ключ можно
// прямо отсюда — уводить в настройки ради одной кнопки незачем.
export function PasskeyNudge() {
  const notify = useNotify()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [snoozed, setSnoozed] = useState(isPasskeyNudgeSnoozed)
  const [error, setError] = useState<string | null>(null)

  const { data: credentials } = useQuery({ queryKey: ['credentials'], queryFn: listCredentials })

  const add = useMutation({
    mutationFn: () => addCredential(DEFAULT_KEY_NAME),
    onSuccess: () => {
      notify.show('Ключ привязан. Вход по паролю отключён', 'success')
      void queryClient.invalidateQueries({ queryKey: ['credentials'] })
    },
    onError: (err) => {
      if (!isAbortedCeremony(err)) setError(getErrorMessage(err))
    },
  })

  const open = credentials !== undefined && credentials.length === 0 && !snoozed

  const handleSnooze = () => {
    snoozePasskeyNudge()
    setSnoozed(true)
  }

  return (
    <Modal
      open={open}
      onClose={handleSnooze}
      title="Перейдите на ключ доступа"
      width={480}
      hideClose
    >
      <div className="passkey-nudge">
        <div className="passkey-nudge__mark" aria-hidden>
          <Fingerprint />
        </div>
        <p className="passkey-nudge__text">
          Пароль можно подсмотреть, подобрать и выманить на поддельной странице. Ключ доступа (Touch
          ID, Face ID или аппаратный ключ) не передаётся никуда и не работает на чужом сайте.
        </p>
        <p className="passkey-nudge__text passkey-nudge__text--dim">
          Займёт несколько секунд. После привязки вход по паролю для вашей учётной записи
          отключается автоматически.
        </p>
        {error ? <ErrorAlert message={error} /> : null}
        <div className="passkey-nudge__actions">
          <Button fullWidth loading={add.isPending} onClick={() => add.mutate()}>
            привязать ключ
          </Button>
          <div className="passkey-nudge__secondary">
            <button
              type="button"
              className="passkey-nudge__link"
              onClick={() => {
                handleSnooze()
                void navigate('/security')
              }}
            >
              настроить в разделе «Безопасность»
            </button>
            <button type="button" className="passkey-nudge__link" onClick={handleSnooze}>
              напомнить завтра
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
